/**
 * État de l'éditeur d'escalade d'un POI « Escalade » : son site, ses parois et leurs voies, et l'outil
 * de carte en cours (placer une paroi, tracer le pied de paroi, placer ou déplacer une voie).
 * La page de carte lui passe les clics (handleMapClick) et dessine ses calques (ClimbingEditLayers).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  createClimbingSite,
  createCrag,
  createRoute,
  deleteCrag,
  deleteRoute,
  fetchClimbing,
  updateClimbingSite,
  updateCrag,
  updateRoute,
  type ClimbingCrag,
  type ClimbingRoute,
  type ClimbingSite,
  type LatLng,
  type RouteInput,
} from '../../../api'
import { nearestOnLine } from '../../../map/geo'

/** Un clic à moins de cette distance du pied de paroi s'y accroche (comme les points des chemins). */
const SNAP_TO_CLIFF_M = 30

export type ClimbingTab = 'fiche' | 'parois' | 'voies'
export type RouteDraft = Omit<RouteInput, 'location'>
export type ClimbingTool =
  | { kind: 'crag'; name: string }
  | { kind: 'line'; cragId: number; points: LatLng[] }
  | { kind: 'route'; cragId: number; draft: RouteDraft }
  | { kind: 'move-route'; routeId: number; cragId: number }
  | { kind: 'move-crag'; cragId: number }
  | null

export const EMPTY_ROUTE: RouteDraft = { name: '', grade: '', styles: [], length_m: null, pitches: null, bolts: null, description: '' }

/** Point le plus proche sur les lignes de la paroi, s'il est assez près ; sinon le clic tel quel. */
export function snapToCliff(crag: ClimbingCrag | undefined, p: LatLng): LatLng {
  let best: { point: LatLng; distM: number } | null = null
  for (const line of crag?.cliff_lines ?? []) {
    if (line.length < 2) continue
    const hit = nearestOnLine(line, p)
    if (!best || hit.distM < best.distM) best = hit
  }
  return best && best.distM <= SNAP_TO_CLIFF_M ? best.point : p
}

/** Voies dans l'ordre du topo : de gauche à droite le long du pied de paroi (sinon par cote). */
export function routesAlong(crag: ClimbingCrag): ClimbingRoute[] {
  const line = crag.cliff_lines.find((l) => l.length >= 2)
  if (!line) return crag.routes
  const pos = (r: ClimbingRoute) => {
    if (!r.location) return Number.POSITIVE_INFINITY
    const hit = nearestOnLine(line, r.location)
    return hit.seg + hit.t
  }
  return [...crag.routes].sort((a, b) => pos(a) - pos(b))
}

export function useClimbingEditor(orgId: number, poiId: number | null) {
  const [site, setSite] = useState<ClimbingSite | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<ClimbingTab>('fiche')
  const [cragId, setCragId] = useState<number | null>(null)
  const [routeId, setRouteId] = useState<number | 'new' | null>(null)
  const [tool, setTool] = useState<ClimbingTool>(null)

  const reload = useCallback(
    async (signal?: AbortSignal) => {
      if (poiId == null) return
      setLoading(true)
      try {
        const data = await fetchClimbing(orgId, poiId, signal)
        setSite(data.site)
      } catch (e) {
        if (!signal?.aborted) setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!signal?.aborted) setLoading(false)
      }
    },
    [orgId, poiId],
  )

  useEffect(() => {
    setSite(null)
    setTab('fiche')
    setCragId(null)
    setRouteId(null)
    setTool(null)
    setError(null)
    if (poiId == null) return
    const ctrl = new AbortController()
    void reload(ctrl.signal)
    return () => ctrl.abort()
  }, [poiId, reload])

  /** Le site est géré par l'organisation quand il est relié à ce POI. */
  const linked = site != null && site.poi_id === poiId
  const crags = useMemo(() => site?.crags ?? [], [site])
  const crag = crags.find((c) => c.id === cragId)

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    setBusy(true)
    setError(null)
    try {
      return await fn()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      return null
    } finally {
      setBusy(false)
    }
  }, [])

  const replaceCrag = (next: ClimbingCrag) =>
    setSite((s) => (s ? { ...s, crags: s.crags.some((c) => c.id === next.id) ? s.crags.map((c) => (c.id === next.id ? next : c)) : [...s.crags, next] } : s))
  const replaceRoute = (cid: number, next: ClimbingRoute) =>
    setSite((s) =>
      s
        ? {
            ...s,
            crags: s.crags.map((c) =>
              c.id === cid ? { ...c, routes: c.routes.some((r) => r.id === next.id) ? c.routes.map((r) => (r.id === next.id ? next : r)) : [...c.routes, next] } : c,
            ),
          }
        : s,
    )

  const actions = {
    createSite: () =>
      run(async () => {
        if (poiId == null) return
        setSite(await createClimbingSite(orgId, poiId))
        setTab('parois')
      }),
    linkSite: () =>
      run(async () => {
        if (!site || poiId == null) return
        setSite(await updateClimbingSite(orgId, site.id, { poi_id: poiId }))
        setTab('parois')
      }),
    saveCrag: (id: number, data: { name: string; description: string; url: string }) => run(async () => replaceCrag(await updateCrag(orgId, id, data))),
    clearLines: (id: number) => run(async () => replaceCrag(await updateCrag(orgId, id, { cliff_lines: [] }))),
    removeCrag: (id: number) =>
      run(async () => {
        await deleteCrag(orgId, id)
        setSite((s) => (s ? { ...s, crags: s.crags.filter((c) => c.id !== id) } : s))
        setCragId(null)
      }),
    saveRoute: (cid: number, id: number, data: Partial<RouteInput>) => run(async () => replaceRoute(cid, await updateRoute(orgId, id, data))),
    removeRoute: (cid: number, id: number) =>
      run(async () => {
        await deleteRoute(orgId, id)
        setSite((s) => (s ? { ...s, crags: s.crags.map((c) => (c.id === cid ? { ...c, routes: c.routes.filter((r) => r.id !== id) } : c)) } : s))
        setRouteId(null)
      }),
    /** Termine le tracé en cours : la nouvelle ligne s'ajoute à celles de la paroi. */
    finishLine: () =>
      run(async () => {
        if (tool?.kind !== 'line') return
        const target = crags.find((c) => c.id === tool.cragId)
        if (!target || tool.points.length < 2) {
          setTool(null)
          return
        }
        replaceCrag(await updateCrag(orgId, target.id, { cliff_lines: [...target.cliff_lines, tool.points] }))
        setTool(null)
      }),
  }

  // Clavier, comme pour les chemins : Entrée termine le tracé, Retour arrière enlève le dernier point,
  // Échap annule l'outil.
  useEffect(() => {
    if (!tool) return
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement | null)?.closest('input, textarea, select')) return
      if (e.key === 'Escape') setTool(null)
      else if (tool.kind === 'line' && e.key === 'Enter') void actions.finishLine()
      else if (tool.kind === 'line' && (e.key === 'Backspace' || e.key === 'Delete')) setTool({ ...tool, points: tool.points.slice(0, -1) })
      else return
      e.stopPropagation()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  })

  /** Clic sur la carte pendant un outil d'escalade. Renvoie vrai s'il est consommé. */
  const handleMapClick = (p: LatLng): boolean => {
    if (!tool || !site) return false
    if (tool.kind === 'line') {
      setTool({ ...tool, points: [...tool.points, p] })
      return true
    }
    if (tool.kind === 'crag') {
      const name = tool.name
      setTool(null)
      void run(async () => {
        const created = await createCrag(orgId, { site_id: site.id, name, location: p })
        replaceCrag(created)
        setCragId(created.id)
      })
      return true
    }
    if (tool.kind === 'move-crag') {
      const id = tool.cragId
      setTool(null)
      void run(async () => replaceCrag(await updateCrag(orgId, id, { location: p })))
      return true
    }
    const target = crags.find((c) => c.id === tool.cragId)
    const at = snapToCliff(target, p)
    if (tool.kind === 'route') {
      const { draft, cragId: cid } = tool
      setTool(null)
      void run(async () => {
        const created = await createRoute(orgId, { ...draft, crag_id: cid, location: at })
        replaceRoute(cid, created)
        setRouteId(created.id)
      })
      return true
    }
    const { routeId: rid, cragId: cid } = tool
    setTool(null)
    void run(async () => replaceRoute(cid, await updateRoute(orgId, rid, { location: at })))
    return true
  }

  return {
    site,
    linked,
    crags,
    crag,
    loading,
    busy,
    error,
    tab,
    setTab,
    cragId,
    setCragId,
    routeId,
    setRouteId,
    tool,
    setTool,
    actions,
    handleMapClick,
  }
}

export type ClimbingEditor = ReturnType<typeof useClimbingEditor>
